import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { gql } from '../../graphql/client';
import { Q_LIST_PACKAGES, Q_RECENT_RELEASE_BUILDS } from '../../graphql/operations';
import { triggerNativeBuild } from '../../lib/platformApi';
import type { AppPackage, AppReleaseBuild, FrontendApp } from '../../types/forge';
import { getAndroidKeystoreStatus } from '../../lib/platformApi';

/**
 * Build section — pre-flight checklist + package picker + native
 * build trigger. Surfaces live build status via a 5s-poll query and
 * exposes a download link once the runner posts an `artifact_url`.
 *
 * Triggering a build calls `POST /build/native { packageId, platform: 'android' }`
 * which in turn auto-creates an app_release row tied to that package
 * and dispatches the runner. The new release shows up in
 * `app_release_build` rows polled below.
 */
export function BuildSection({
  appId,
  app,
}: {
  appId: string;
  app: FrontendApp;
}) {
  const packages = useQuery({
    queryKey: ['packages', appId],
    queryFn: () => gql<{ app_package: AppPackage[] }>(Q_LIST_PACKAGES, { appId }),
  });

  const builds = useQuery({
    queryKey: ['release-builds', appId],
    queryFn: () =>
      gql<{ app_release_build: (AppReleaseBuild & { build_type: string; app_release: { id: string; name: string } })[] }>(
        Q_RECENT_RELEASE_BUILDS,
        { appId },
      ),
    // Conditional polling: only poll while there's an in-progress build
    // (queued / building / submitting). Once everything is in a terminal
    // state (ready / failed / submitted), polling stops. Saves Hasura
    // load on the deployed dev environment where this page might sit
    // mounted in a background tab indefinitely.
    refetchInterval: (query) => {
      const rows = query.state.data?.app_release_build ?? [];
      const inProgress = rows.some(
        (b) => b.status === 'queued' || b.status === 'building' || b.status === 'submitting',
      );
      return inProgress ? 10_000 : false;
    },
  });

  const keystoreStatus = useQuery({
    queryKey: ['android-keystore-status'],
    queryFn: getAndroidKeystoreStatus,
  });

  const [selectedPkg, setSelectedPkg] = useState<string>('');
  const [triggerError, setTriggerError] = useState<string | null>(null);
  /**
   * When on, the build is signed with the bundled default developer
   * keystore (forge-native/scripts/forge-default.keystore, alias
   * `forgedev`). Lets authors verify the full pipeline before they
   * commit to uploading their own signing creds — at the cost of
   * producing an APK that is NOT publishable to the Play Store.
   */
  const [useDefaultSigning, setUseDefaultSigning] = useState(false);

  // Default the package picker to the latest package whenever the
  // list refreshes and the user hasn't picked something else yet.
  useEffect(() => {
    const latest = packages.data?.app_package?.[0]?.id;
    if (latest && !selectedPkg) setSelectedPkg(latest);
  }, [packages.data, selectedPkg]);

  const trigger = useMutation({
    mutationFn: () =>
      triggerNativeBuild({
        packageId: selectedPkg,
        platform: 'android',
        useDefaultSigning,
      }),
    onSuccess() {
      setTriggerError(null);
    },
    onError(err: Error) {
      setTriggerError(err.message);
    },
  });

  const checklist: { label: string; ok: boolean; optional?: boolean }[] = [
    {
      label: 'App name',
      ok: Boolean(app.name && app.name.trim().length > 0),
    },
    {
      label: 'Android package (e.g. com.acme.app)',
      ok: Boolean(app.android_package && app.android_package.trim().length > 0),
    },
    {
      label: 'App icon',
      ok: Boolean(app.icon_asset_url && app.icon_asset_url.trim().length > 0),
    },
    {
      // Optional: the runner falls back to the platform default
      // SplashTheme when no splash asset is provided, so we surface
      // it as a soft warning rather than a hard gate.
      label: 'Splash screen (optional)',
      ok: Boolean(app.splash_asset_url && app.splash_asset_url.trim().length > 0),
      optional: true,
    },
    {
      label: useDefaultSigning
        ? 'Default developer keystore (test only)'
        : 'Android signing keystore',
      ok: useDefaultSigning || keystoreStatus.data?.uploaded === true,
    },
    {
      label: 'At least one package',
      ok: (packages.data?.app_package?.length ?? 0) > 0,
    },
  ];

  const allReady = checklist.every((c) => c.ok || c.optional);

  const recent = builds.data?.app_release_build ?? [];

  return (
    <section style={s.card}>
      <h2 style={s.cardTitle}>Build Android APK</h2>
      <p style={s.cardHint}>
        Compiles a release-signed Android APK with the app's icon, splash
        screen, name, and applicationId baked in. Each build creates its
        own release row.
      </p>

      <div style={s.checklist}>
        {checklist.map((item) => {
          const dotColor = item.ok
            ? '#10B981'
            : item.optional
              ? '#F59E0B'
              : '#DC2626';
          return (
            <div key={item.label} style={s.checklistRow}>
              <span
                style={{
                  ...s.checklistDot,
                  backgroundColor: dotColor,
                }}
              />
              <span style={item.ok ? s.checklistOk : s.checklistMissing}>{item.label}</span>
            </div>
          );
        })}
      </div>

      <label style={s.toggleRow}>
        <input
          type="checkbox"
          checked={useDefaultSigning}
          onChange={(e) => setUseDefaultSigning(e.target.checked)}
          style={s.toggleInput}
        />
        <div style={s.toggleBody}>
          <span style={s.toggleLabel}>Use default developer keystore</span>
          <span style={s.toggleHint}>
            Skips the keystore upload requirement. Output APK is signed with
            Forge's bundled <code>forgedev</code> key — installable on a
            device for testing, but not Play Store-publishable.
          </span>
        </div>
      </label>

      <div style={s.triggerRow}>
        <select
          value={selectedPkg}
          onChange={(e) => setSelectedPkg(e.target.value)}
          style={s.select}
          disabled={(packages.data?.app_package?.length ?? 0) === 0}
        >
          {(packages.data?.app_package ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              v{p.version}
              {p.package_status !== 'ready' ? ` (${p.package_status})` : ''}
            </option>
          ))}
          {(packages.data?.app_package?.length ?? 0) === 0 && (
            <option value="">No packages — Create one in the View Builder</option>
          )}
        </select>
        <button
          type="button"
          onClick={() => trigger.mutate()}
          disabled={!allReady || !selectedPkg || trigger.isPending}
          style={allReady && selectedPkg ? s.buildBtn : s.buildBtnDisabled}
        >
          {trigger.isPending ? 'Dispatching…' : 'Build Android APK'}
        </button>
      </div>
      {triggerError && <p style={s.error}>{triggerError}</p>}

      <div style={s.recent}>
        <h3 style={s.recentTitle}>Recent builds</h3>
        {recent.length === 0 && <p style={s.muted}>None yet.</p>}
        {recent.map((b) => (
          <div key={b.id} style={s.recentRow}>
            <span
              style={{
                ...s.recentDot,
                backgroundColor: STATUS_COLORS[b.status] ?? '#94A3B8',
              }}
            />
            <span style={s.recentName}>{b.app_release.name}</span>
            <span style={s.recentType}>{b.build_type}</span>
            <span style={s.recentStatus}>{b.status}</span>
            <span style={s.recentTime}>
              {new Date(b.updated_at).toLocaleString()}
            </span>
            {b.artifact_url && b.status === 'ready' && (
              <a
                href={b.artifact_url}
                target="_blank"
                rel="noreferrer noopener"
                style={s.downloadLink}
              >
                Download APK
              </a>
            )}
            {b.error_message && (
              <span style={s.recentError} title={b.error_message}>
                error
              </span>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

const STATUS_COLORS: Record<string, string> = {
  queued: '#94A3B8',
  building: '#F59E0B',
  ready: '#10B981',
  submitting: '#F59E0B',
  submitted: '#10B981',
  failed: '#DC2626',
};

const s: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: '#fff',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: 20,
    marginBottom: 16,
  },
  cardTitle: { margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#0F172A' },
  cardHint: { margin: '0 0 16px', fontSize: 12, color: '#94A3B8' },
  checklist: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 },
  checklistRow: { display: 'flex', alignItems: 'center', gap: 8 },
  checklistDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  checklistOk: { fontSize: 12, color: '#0F172A' },
  checklistMissing: { fontSize: 12, color: '#64748B' },
  toggleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '12px 14px',
    marginBottom: 12,
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: 6,
    cursor: 'pointer',
  },
  toggleInput: { marginTop: 2, flexShrink: 0 },
  toggleBody: { display: 'flex', flexDirection: 'column', gap: 2 },
  toggleLabel: { fontSize: 12, fontWeight: 600, color: '#0F172A' },
  toggleHint: { fontSize: 11, color: '#64748B', lineHeight: 1.4 },
  triggerRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  select: {
    flex: 1,
    padding: '8px 10px',
    fontSize: 13,
    border: '1px solid #CBD5E1',
    borderRadius: 4,
  },
  buildBtn: {
    padding: '10px 18px',
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#10B981',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  buildBtnDisabled: {
    padding: '10px 18px',
    fontSize: 13,
    fontWeight: 600,
    color: '#94A3B8',
    backgroundColor: '#E2E8F0',
    border: 'none',
    borderRadius: 6,
    cursor: 'default',
  },
  error: { fontSize: 12, color: '#c00', marginTop: 4 },
  muted: { fontSize: 12, color: '#94A3B8' },
  recent: {
    marginTop: 20,
    paddingTop: 14,
    borderTop: '1px solid #F1F5F9',
  },
  recentTitle: { margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  recentRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 11 },
  recentDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  recentName: { width: 140, fontWeight: 600, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  recentType: { width: 90, color: '#64748B', fontFamily: 'ui-monospace, monospace', fontSize: 10 },
  recentStatus: { flex: 1, color: '#475569' },
  recentTime: { fontSize: 10, color: '#94A3B8' },
  downloadLink: { fontSize: 11, color: '#4F46E5', fontWeight: 600, textDecoration: 'none', marginLeft: 8 },
  recentError: { color: '#DC2626', fontWeight: 700, cursor: 'help', marginLeft: 6 },
};
